import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useNotification } from '@/components/notifications';
import {
  useCreateTriggerConnection,
  useInitiateConnectionAuth,
} from '@/hooks/mutations/triggers/use-trigger-mutations';
import { triggerConnectionsQueryKey } from '@/hooks/queries/triggers/use-list-trigger-connections';
import { useOAuthConsentPopup } from '@/hooks/use-oauth-consent-popup';
import { listTriggerConnections } from '@/services/triggers/triggers';
import { isConsentSettled } from './connection-state';

const WAIT_KEY = 'drive';

/**
 * Start Google consent in a popup and wait for the connection to go live.
 *
 * Consent runs in a separate window so the caller never unmounts — this is
 * reachable from the workflow editor, where a full-page redirect would discard
 * unsaved graph edits.
 */
export const useConnectGoogleDrive = (orgId?: string, myEmail?: string) => {
  const notification = useNotification();
  const queryClient = useQueryClient();
  const createConnection = useCreateTriggerConnection(orgId);
  const reauth = useInitiateConnectionAuth(orgId);
  const consent = useOAuthConsentPopup('gdrive-oauth');

  const run = useCallback(
    async (
      authorize: () => Promise<{ id: string; redirectUrl?: string | null }>,
    ) => {
      try {
        await consent.start(
          WAIT_KEY,
          async () => {
            const { id, redirectUrl } = await authorize();
            if (!redirectUrl) {
              throw new Error('No Google authorization URL was returned');
            }
            return {
              redirectUrl,
              isSettled: async () => {
                const fresh = await listTriggerConnections(orgId!);
                const mine = fresh.find((c) => c.id === id);
                if (!mine || !isConsentSettled(mine)) return false;
                // Push the rows we already have so the UI updates without a refetch.
                queryClient.setQueryData(
                  triggerConnectionsQueryKey(orgId),
                  fresh,
                );
                return true;
              },
            };
          },
          {
            onConnected: () => notification.success?.('Google Drive connected'),
            onTimedOut: () =>
              notification.error(
                'Google Drive authorization timed out. Try connecting again.',
              ),
            // Abandoning is silent: closing the window is a deliberate cancel,
            // and the UI still offers to finish authorization.
          },
        );
      } catch (error) {
        Sentry.captureException(error, { extra: { orgId, myEmail } });
        notification.error(
          error instanceof Error
            ? error.message
            : 'Could not start Google Drive authorization',
        );
      }
    },
    [consent, notification, orgId, myEmail, queryClient],
  );

  const connect = useCallback(
    () =>
      run(async () => {
        const connection = await createConnection.mutateAsync({
          name: 'Google Drive',
          popup: true,
        });
        return { id: connection.id, redirectUrl: connection.redirect_url };
      }),
    [run, createConnection],
  );

  /** Re-run consent for a connection that exists but cannot reach Drive. */
  const reconnect = useCallback(
    (connectionId: string) =>
      run(async () => {
        const result = await reauth.mutateAsync({ connectionId, popup: true });
        return { id: connectionId, redirectUrl: result.redirect_url };
      }),
    [run, reauth],
  );

  return {
    connect,
    reconnect,
    isConnecting:
      createConnection.isPending ||
      reauth.isPending ||
      consent.isWaiting(WAIT_KEY),
  };
};
